import { useEffect,useMemo,useState } from "react";
import { Box,Typography,Button,TextField,Table,TableBody,TableCell,TableContainer,TableHead,TableRow,CircularProgress,Dialog,DialogTitle,DialogContent,DialogActions,Switch } from "@mui/material";
import { useAppTheme } from "../../context/ThemeContext";
import { useAlert } from "../../context/AlertContext";
import RefereeCard from "./AdminReferees/RefereeCard";

const getCookie=(name)=>{
  const value=document.cookie.split("; ").find((row)=>row.startsWith(`${name}=`));
  return value?decodeURIComponent(value.split("=")[1]):"";
};

const readResponse=async(response)=>{
  const contentType=response.headers.get("content-type")||"";
  if(contentType.includes("application/json")) return response.json();
  if(!response.ok) throw new Error("El servidor no respondió correctamente.");
  return {};
};

const emptyForm={
  full_name:"",
  identification:"",
  phone:"",
  active:true,
};

export default function AdminReferees(){
  const {colors,typography,shape}=useAppTheme();
  const {showSuccess,showError}=useAlert();

  const [referees,setReferees]=useState([]);
  const [search,setSearch]=useState("");
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [modalOpen,setModalOpen]=useState(false);
  const [selectedReferee,setSelectedReferee]=useState(null);
  const [cardReferee,setCardReferee]=useState(null);
  const [form,setForm]=useState(emptyForm);

  const loadReferees=async()=>{
    try{
      setLoading(true);

      const response=await fetch("/matches/referees/",{
        credentials:"include"
      });

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(
          data.error||
          data.detail||
          "No se pudieron cargar los árbitros."
        );
      }

      setReferees(
        Array.isArray(data)?
        data:
        data.results||[]
      );
    }catch(error){
      showError(
        error.message||
        "No se pudieron cargar los árbitros."
      );
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    loadReferees();
  },[]);

  const filteredReferees=useMemo(()=>{
    const value=search.trim().toLowerCase();

    if(!value) return referees;

    return referees.filter((referee)=>{
      return [
        referee.full_name,
        referee.identification,
        referee.phone
      ].some((field)=>
        String(field||"")
          .toLowerCase()
          .includes(value)
      );
    });
  },[referees,search]);

  const openCreate=()=>{
    setSelectedReferee(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit=(referee)=>{
    setSelectedReferee(referee);

    setForm({
      full_name:referee.full_name||"",
      identification:referee.identification||"",
      phone:referee.phone||"",
      active:referee.active!==false
    });

    setModalOpen(true);
  };

  const closeModal=()=>{
    if(saving) return;

    setModalOpen(false);
    setSelectedReferee(null);
    setForm(emptyForm);
  };

  const handleChange=(event)=>{
    const {name,value}=event.target;

    setForm((current)=>({
      ...current,
      [name]:value
    }));
  };

  const handleSave=async()=>{
    const fullName=form.full_name.trim();
    const identification=form.identification.trim();
    const phone=form.phone.trim();

    if(!fullName){
      showError("Los nombres completos son obligatorios.");
      return;
    }

    if(!identification){
      showError("La identificación es obligatoria.");
      return;
    }

    try{
      setSaving(true);

      const url=selectedReferee?
        `/matches/referees/${selectedReferee.qr_token}/`:
        "/matches/referees/";

      const response=await fetch(url,{
        method:selectedReferee?"PATCH":"POST",
        credentials:"include",
        headers:{
          "Content-Type":"application/json",
          "X-CSRFToken":getCookie("csrftoken")
        },
        body:JSON.stringify({
          full_name:fullName,
          identification,
          phone,
          active:form.active
        })
      });

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(
          data.error||
          data.detail||
          "No se pudo guardar el árbitro."
        );
      }

      const wasEditing=Boolean(selectedReferee);

      setModalOpen(false);
      setSelectedReferee(null);
      setForm(emptyForm);

      await loadReferees();

      showSuccess(
        wasEditing?
        "Árbitro actualizado correctamente.":
        "Árbitro creado correctamente."
      );
    }catch(error){
      showError(
        error.message||
        "No se pudo guardar el árbitro."
      );
    }finally{
      setSaving(false);
    }
  };

  const handleActiveChange=async(referee)=>{
    try{
      const response=await fetch(
        `/matches/referees/${referee.qr_token}/`,
        {
          method:"PATCH",
          credentials:"include",
          headers:{
            "Content-Type":"application/json",
            "X-CSRFToken":getCookie("csrftoken")
          },
          body:JSON.stringify({
            active:!referee.active
          })
        }
      );

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(
          data.error||
          data.detail||
          "No se pudo actualizar el estado."
        );
      }

      setReferees((current)=>
        current.map((item)=>
          item.qr_token===referee.qr_token?
          {
            ...item,
            active:data.active
          }:
          item
        )
      );

      showSuccess(
        data.active?
        "Árbitro activado correctamente.":
        "Árbitro desactivado correctamente."
      );
    }catch(error){
      showError(
        error.message||
        "No se pudo actualizar el estado."
      );
    }
  };

  return (
    <Box sx={{width:"100%",maxWidth:1300,mx:"auto"}}>
      <Box
        sx={{
          display:"flex",
          flexDirection:{xs:"column",sm:"row"},
          alignItems:{xs:"stretch",sm:"center"},
          justifyContent:"space-between",
          gap:1.5,
          mb:2.5
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize:{
                xs:typography.sizes.xl,
                md:typography.sizes.pageTitle
              },
              fontWeight:700,
              color:colors.text
            }}
          >
            Árbitros
          </Typography>

          <Typography
            sx={{
              mt:0.4,
              fontSize:typography.sizes.md,
              color:colors.textSecondary
            }}
          >
            Administra los árbitros disponibles para los partidos.
          </Typography>
        </Box>

        <Button
          variant="contained"
          size="small"
          onClick={openCreate}
          sx={{
            px:2.2,
            textTransform:"none",
            backgroundColor:colors.secondary,
            "&:hover":{
              backgroundColor:colors.secondaryHover
            }
          }}
        >
          Nuevo árbitro
        </Button>
      </Box>

      <Box
        sx={{
          mb:1.5,
          display:"flex",
          justifyContent:"flex-end"
        }}
      >
        <TextField
          size="small"
          value={search}
          onChange={(event)=>setSearch(event.target.value)}
          placeholder="Buscar árbitro"
          sx={{
            width:{xs:"100%",sm:320},
            "& .MuiOutlinedInput-root":{
              backgroundColor:colors.surface
            }
          }}
        />
      </Box>

      <TableContainer
        sx={{
          border:`1px solid ${colors.border}`,
          borderRadius:shape.radiusLarge,
          backgroundColor:colors.surface,
          boxShadow:"none",
          overflowX:"auto"
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{backgroundColor:colors.tableHeader}}>
              <TableCell
                sx={{
                  color:colors.tableHeaderText,
                  fontWeight:700
                }}
              >
                Árbitro
              </TableCell>

              <TableCell
                sx={{
                  color:colors.tableHeaderText,
                  fontWeight:700
                }}
              >
                Identificación
              </TableCell>

              <TableCell
                sx={{
                  color:colors.tableHeaderText,
                  fontWeight:700
                }}
              >
                Teléfono
              </TableCell>

              <TableCell
                align="center"
                sx={{
                  color:colors.tableHeaderText,
                  fontWeight:700
                }}
              >
                Activo
              </TableCell>

              <TableCell
                align="right"
                sx={{
                  color:colors.tableHeaderText,
                  fontWeight:700
                }}
              >
                Acciones
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading?(
              <TableRow>
                <TableCell
                  colSpan={5}
                  align="center"
                  sx={{py:5}}
                >
                  <CircularProgress
                    size={26}
                    sx={{color:colors.secondary}}
                  />
                </TableCell>
              </TableRow>
            ):filteredReferees.length===0?(
              <TableRow>
                <TableCell
                  colSpan={5}
                  align="center"
                  sx={{
                    py:5,
                    color:colors.textSecondary
                  }}
                >
                  No hay árbitros registrados.
                </TableCell>
              </TableRow>
            ):(
              filteredReferees.map((referee)=>(
                <TableRow
                  key={referee.qr_token}
                  hover
                  sx={{
                    "&:last-child td":{
                      borderBottom:0
                    }
                  }}
                >
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize:typography.sizes.md,
                        fontWeight:600,
                        color:colors.text
                      }}
                    >
                      {referee.full_name}
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{
                      fontSize:typography.sizes.md,
                      color:colors.text
                    }}
                  >
                    {referee.identification}
                  </TableCell>

                  <TableCell
                    sx={{
                      fontSize:typography.sizes.md,
                      color:colors.textSecondary
                    }}
                  >
                    {referee.phone||"—"}
                  </TableCell>

                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={referee.active!==false}
                      onChange={()=>handleActiveChange(referee)}
                    />
                  </TableCell>

                  <TableCell align="right">
                    <Box
                      sx={{
                        display:"flex",
                        justifyContent:"flex-end",
                        alignItems:"center",
                        gap:0.5
                      }}
                    >
                      <Button
                        size="small"
                        onClick={()=>setCardReferee(referee)}
                        sx={{
                          px:1.2,
                          minWidth:0,
                          textTransform:"none",
                          color:colors.secondary
                        }}
                      >
                        Carnet
                      </Button>

                      <Button
                        size="small"
                        onClick={()=>openEdit(referee)}
                        sx={{
                          px:1.2,
                          minWidth:0,
                          textTransform:"none",
                          color:colors.secondary
                        }}
                      >
                        Editar
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={modalOpen}
        onClose={closeModal}
        fullWidth
        maxWidth="sm"
        disableScrollLock
        PaperProps={{
          sx:{
            borderRadius:shape.radiusLarge,
            boxShadow:"none",
            border:`1px solid ${colors.border}`,
            backgroundColor:colors.surface,
            backgroundImage:"none"
          }
        }}
      >
        <DialogTitle>
          <Typography
            sx={{
              fontSize:typography.sizes.xl,
              fontWeight:700,
              color:colors.text
            }}
          >
            {selectedReferee?"Editar árbitro":"Nuevo árbitro"}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{
              pt:1,
              display:"flex",
              flexDirection:"column",
              gap:1.5
            }}
          >
            <TextField
              size="small"
              name="full_name"
              label="Nombres completos"
              value={form.full_name}
              onChange={handleChange}
              fullWidth
              required
            />

            <TextField
              size="small"
              name="identification"
              label="Identificación"
              value={form.identification}
              onChange={handleChange}
              fullWidth
              required
            />

            <TextField
              size="small"
              name="phone"
              label="Teléfono"
              value={form.phone}
              onChange={handleChange}
              fullWidth
            />

            {selectedReferee&&(
              <Box
                sx={{
                  display:"flex",
                  alignItems:"center",
                  justifyContent:"space-between",
                  gap:2,
                  px:1.5,
                  py:0.7,
                  border:`1px solid ${colors.border}`,
                  borderRadius:shape.radius
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize:typography.sizes.md,
                      fontWeight:600,
                      color:colors.text
                    }}
                  >
                    Árbitro activo
                  </Typography>

                  <Typography
                    sx={{
                      mt:0.2,
                      fontSize:typography.sizes.sm,
                      color:colors.textSecondary
                    }}
                  >
                    Disponible para ser asignado a partidos.
                  </Typography>
                </Box>

                <Switch
                  size="small"
                  checked={form.active}
                  onChange={(event)=>
                    setForm((current)=>({
                      ...current,
                      active:event.target.checked
                    }))
                  }
                />
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px:3,
            py:2,
            borderTop:`1px solid ${colors.border}`
          }}
        >
          <Button
            size="small"
            onClick={closeModal}
            disabled={saving}
            sx={{
              color:colors.buttonText,
              textTransform:"none"
            }}
          >
            Cancelar
          </Button>

          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              px:2.2,
              textTransform:"none",
              backgroundColor:colors.secondary,
              "&:hover":{
                backgroundColor:colors.secondaryHover
              }
            }}
          >
            {saving?"Guardando...":"Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      <RefereeCard
        open={Boolean(cardReferee)}
        referee={cardReferee}
        onClose={()=>setCardReferee(null)}
      />
    </Box>
  );
}